/**
 * Epics administration surface.
 *
 * Admins pick a workspace, review its epics, rename/redescribe one via
 * PATCH /epics/<id>/, and archive (delete) any epic via DELETE /epics/<id>/.
 * Deleting an epic soft-archives it on the backend.
 * @component
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import api from '../../services/api';
import useCurrentUser from '../../utils/useCurrentUser';
import { useWorkspace } from '../../utils/WorkspaceContext';

const EpicsSettings = () => {
  const { isAdmin, loading: userLoading } = useCurrentUser();
  const { workspaces, currentWorkspace } = useWorkspace();

  const [workspaceId, setWorkspaceId] = useState(currentWorkspace?.id || '');
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmEpic, setConfirmEpic] = useState(null);
  const [toast, setToast] = useState(null);

  // The epic being edited, held as a draft so Cancel leaves the list untouched.
  const [editEpic, setEditEpic] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const openEdit = (epic) => {
    setEditEpic(epic);
    setEditForm({ name: epic.name || '', description: epic.description || '' });
  };

  const fetchEpics = () => {
    if (!workspaceId) {
      setEpics([]);
      return;
    }
    setLoading(true);
    api
      .get('epics/', { params: { workspace_id: workspaceId } })
      .then((res) => setEpics(res.data || []))
      .catch(() => setToast({ severity: 'error', message: 'Could not load epics.' }))
      .finally(() => setLoading(false));
  };

  useEffect(fetchEpics, [workspaceId]);

  const handleSave = async () => {
    if (!editEpic) return;
    const name = editForm.name.trim();
    if (!name) {
      setToast({ severity: 'error', message: 'An epic needs a name.' });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`epics/${editEpic.id}/`, {
        name,
        description: editForm.description.trim(),
      });
      setEpics((prev) => prev.map((e) => (e.id === editEpic.id ? data : e)));
      setToast({ severity: 'success', message: `Updated "${data.name}".` });
      setEditEpic(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update epic.';
      setToast({ severity: 'error', message: detail });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmEpic) return;
    setDeleting(true);
    try {
      await api.delete(`epics/${confirmEpic.id}/`);
      setEpics((prev) => prev.filter((e) => e.id !== confirmEpic.id));
      setToast({ severity: 'success', message: `Deleted "${confirmEpic.name}".` });
      setConfirmEpic(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to delete epic.';
      setToast({ severity: 'error', message: detail });
    } finally {
      setDeleting(false);
    }
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return <Alert severity="info">Only admins can manage epics.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Epics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review, edit and delete epics in a workspace. Deleting an epic
          archives it.
        </Typography>
      </Box>

      <FormControl fullWidth size="small" sx={{ mb: 3, maxWidth: 360 }}>
        <InputLabel id="epics-workspace-label">Workspace</InputLabel>
        <Select
          labelId="epics-workspace-label"
          label="Workspace"
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
        >
          {(workspaces || []).map((ws) => (
            <MenuItem key={ws.id} value={ws.id}>
              {ws.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : epics.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No epics in this workspace.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {epics.map((epic, idx) => (
              <ListItem
                key={epic.id}
                divider={idx < epics.length - 1}
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit epic" arrow>
                      <IconButton onClick={() => openEdit(epic)}>
                        <EditOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete epic" arrow>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => setConfirmEpic(epic)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={epic.name}
                  secondary={epic.description || null}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Dialog
        open={Boolean(editEpic)}
        onClose={() => !saving && setEditEpic(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit epic</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={editForm.description}
            onChange={(e) =>
              setEditForm((f) => ({ ...f, description: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEpic(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editForm.name.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmEpic)} onClose={() => !deleting && setConfirmEpic(null)}>
        <DialogTitle>Delete epic?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{confirmEpic?.name}</strong>? This will archive the epic.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEpic(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default EpicsSettings;
